/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 31st 2022, 8:17:14 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 * The APIOne2ManyListEditorFormInput should be used for an API schema that has a one2many relationship with
 * another schema. This is an inverse of the Many2One relationship. Since we do not know upfront the
 * primary keys of the multiple items in the one2Many relationship (remember one2many is a 'virtual'
 * relationship that is not a property of the data itself but rather a property of the relationship),
 * this component takes the  primary key of the 'One' side of the cardinality as the foreignKey prop.
 * This prop is then used to send a get request to an endpoint that should filter the 'Many' objects of
 * the cardinility. The fetched list is then populated in our ListEditorProvider for editing and a renderprop
 * is used by the calling component to render how the List View looks liked
 *
 */

import { ListEditorProvider, ListEditorProviderProps, useListEditorContext, IListEditorContext } from "lib/providers";
import { ObjectKey, PrimaryKeyValue } from "lib/types";
import { OSFormInputProps } from "ui-widgets/forms/types";
import { APIListProgressWrapperProps } from "ui-widgets/wrappers/APIRequestWrappers";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";

/*interface ListViewRenderPropOptions<TRelatedObject>
  extends Pick<
    IListEditorContext<TRelatedObject>,
    | "itemsListState"
    | "currentMutationItem"
    | "updateList"
    | "updateListAndSelectIfNewItem"
    | "selectItem"
    | "createNewItem"
    | "creationQueueHasItems"
    | "endOfListPosition"
    | "removeItem"
  > {}
*/

interface ListViewRenderProp<TRelatedObject> {
  renderListView: (options: IListEditorContext<TRelatedObject>) => JSX.Element;
}

interface RelatedObjectListEditorProps<TPrimaryObject, TRelatedObject>
  extends Omit<ListEditorProviderProps<TRelatedObject, false>, "children" | "apiQueryParams"> {
  foreignKey: ObjectKey<TRelatedObject>;
  foreignKeyId: PrimaryKeyValue<TPrimaryObject>;
}

// Since One2Many is an inverse of Many2One, most likely, the primary object will not have a reference key to the related object. It is the related
// object that has a foreign key to the primary object in this inverse scenario. And thus they reason we are requiring
// the mandatory apiQueryParams that filter by that foreign key.
type One2ManyInputProps<TPrimaryObject> = Partial<Pick<OSFormInputProps<TPrimaryObject>, "schema_field_key">> &
  Omit<OSFormInputProps<TPrimaryObject>, "schema_field_key">;

export type GenericAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject> =
  One2ManyInputProps<TPrimaryObject> &
    RelatedObjectListEditorProps<TPrimaryObject, TRelatedObject> &
    ListViewRenderProp<TRelatedObject> &
    Pick<APIListProgressWrapperProps<TRelatedObject>, "noDataMessage" | "errorMessage">;

export type ConcreteAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject> = Omit<
  GenericAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject>,
  "apiServicePath" | "apiQueryParams" | "itemIdProperty" | "getOptionLabel" | "getOptionComponent"
>;

export const APIOne2ManyListEditorFormInput = <TPrimaryObject, TRelatedObject>(
  props: GenericAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject>
) => {
  return props.foreignKeyId ? (
    <ListEditorProvider<TRelatedObject, false>
      uniqueIdentifier={props.uniqueIdentifier}
      apiServicePath={props.apiServicePath}
      apiQueryParams={{ [props.foreignKey]: props.foreignKeyId }}
    >
      <One2ManyListView
        renderListView={props.renderListView}
        uniqueIdentifier={props.uniqueIdentifier}
        noDataMessage={props.noDataMessage}
      />
    </ListEditorProvider>
  ) : null;
};

const One2ManyListView = <TPrimaryObject, TRelatedObject>({
  renderListView,
  noDataMessage,
}: Pick<
  GenericAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject>,
  "renderListView" | "uniqueIdentifier" | "noDataMessage"
>) => {
  const listEditorContext = useListEditorContext<TRelatedObject>();

  return (
    <APIListProgressWrapper
      showMessageWhenEmpty={Boolean(noDataMessage)}
      items={listEditorContext.itemsListState.savedItems}
      showLoader={listEditorContext.isFetchingItems}
      httpResponseStatusCode={listEditorContext.fetchItemsStatusCode}
      isAPIError={listEditorContext.fetchItemsHasError}
      noDataMessage={noDataMessage}
    >
      {renderListView(listEditorContext)}
    </APIListProgressWrapper>
  );
};
